import { useCallback, useMemo } from "react";
import { useStore } from "zustand";
import {
  fetchDevices,
  initializeMedia,
  selectAudioInput,
  selectVideoInput,
  toggleAudio,
  toggleVideo,
} from "./device-controller";
import {
  useMediaMonitor,
  useUserMediaStream,
  userMediaStore,
} from "./media-monitor";

export const useUserMedia = () => {
  const monitor = useMediaMonitor();
  const userMediaStream = useUserMediaStream();
  const devices = useStore(userMediaStore, (s) => s.devices);
  const initializationState = useStore(userMediaStore, (s) => s.initializationState);
  const abortController = useStore(userMediaStore, (s) => s.abortController);

  const stopMedia = useCallback(() => {
    if (userMediaStream) {
      userMediaStream.getTracks().forEach((track) => track.stop());
    }
  }, [userMediaStream]);

  const selectAudioSource = useCallback(
    (deviceId: string) => {
      selectAudioInput(deviceId);
    },
    [],
  );

  const selectVideoSource = useCallback(
    (deviceId: string) => {
      selectVideoInput(deviceId);
    },
    [],
  );

  const isAudioActive = useMemo(() => {
    const tracks = userMediaStream?.getAudioTracks() ?? [];
    return tracks.length > 0 && tracks.every((track) => track.enabled);
  }, [userMediaStream]);
  const isVideoActive = useMemo(() => {
    const tracks = userMediaStream?.getVideoTracks() ?? [];
    return tracks.length > 0 && tracks.every((track) => track.enabled);
  }, [userMediaStream]);
  const stream = useMemo(() => userMediaStream || null, [userMediaStream]);
  return {
    // State
    devices,
    stream,
    status: devices.audio.health.status || "idle",
    error: devices.audio.health.error || null,
    isAudioActive: isAudioActive,
    isVideoActive: isVideoActive,
    audioDeviceId: devices.audio.activeDeviceId || null,
    videoDeviceId: devices.video.activeDeviceId || null,
    initializationState,
    monitor,
    abortController,

    // Functions
    getDevices: fetchDevices,
    selectAudioSource,
    selectVideoSource,
    stopMedia,
    initialize: initializeMedia,
    toggleAudio,
    toggleVideo,
  };
};
